import React, { useState, useEffect } from 'react';
import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, IonButtons, IonBackButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonButton, IonBadge } from '@ionic/react';
import SentryDebugButton from '../components/SentryDebugButton';
import * as Sentry from '@sentry/react';
import env from '../utils/loadEnv';

/**
 * Page to test Sentry integration
 * Mobile-first design following app styling
 */
const SentryTestPage: React.FC = () => {
  const [dsnStatus, setDsnStatus] = useState<'valid' | 'invalid' | 'checking'>('checking');
  const [networkStatus, setNetworkStatus] = useState<'online' | 'offline'>('online');
  const [sentryInitialized, setSentryInitialized] = useState(false);

  // Check DSN validity and network status
  useEffect(() => {
    // Check if DSN is valid
    const dsn = env.VITE_SENTRY_DSN;
    if (dsn && dsn.includes('sentry.io') && dsn.includes('@')) {
      setDsnStatus('valid');
    } else {
      setDsnStatus('invalid');
    }

    // Check if Sentry is initialized - more reliable method
    try {
      // Try to use a Sentry method that only works when initialized
      const testId = Sentry.captureMessage('Testing Sentry initialization', {
        level: 'debug',
      });
      console.log('Sentry test ID:', testId);
      setSentryInitialized(!!testId);
    } catch (error) {
      console.error('Sentry initialization check failed:', error);
      setSentryInitialized(false);
    }

    // Check network status
    const handleOnline = () => setNetworkStatus('online');
    const handleOffline = () => setNetworkStatus('offline');

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);



  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/" />
          </IonButtons>
          <IonTitle>Sentry Test</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <div className="max-w-md mx-auto">
          <div className="mb-4 text-center">
            <h1 className="text-2xl font-bold">Sentry Integration Test</h1>
            <p className="text-sm text-gray-500 mt-2">
              Verify your Sentry integration is working properly
            </p>
          </div>

          {/* Status Card */}
          <IonCard className="mb-4">
            <IonCardHeader>
              <IonCardTitle>Sentry Status</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span>DSN Configuration:</span>
                  {dsnStatus === 'valid' ? (
                    <IonBadge color="success">Valid</IonBadge>
                  ) : dsnStatus === 'invalid' ? (
                    <IonBadge color="danger">Invalid</IonBadge>
                  ) : (
                    <IonBadge color="warning">Checking...</IonBadge>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <span>Network Status:</span>
                  {networkStatus === 'online' ? (
                    <IonBadge color="success">Online</IonBadge>
                  ) : (
                    <IonBadge color="danger">Offline</IonBadge>
                  )}
                </div>

                <div className="flex justify-between items-center">
                  <span>Sentry Initialized:</span>
                  {sentryInitialized ? (
                    <IonBadge color="success">Yes</IonBadge>
                  ) : (
                    <IonBadge color="danger">No</IonBadge>
                  )}
                </div>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Quick Test Button */}
          <div className="mb-6">
            <SentryDebugButton />
          </div>

          {/* Troubleshooting Guide */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Troubleshooting</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="space-y-3 text-sm">
                <p className="font-medium">If events aren't showing in Sentry:</p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>Check browser console for errors</li>
                  <li>Verify your DSN is correct in <code>.env.development</code></li>
                  <li>Ensure you're not blocking requests with ad blockers</li>
                  <li>Try disabling any content security policies</li>
                  <li>Check network tab for blocked requests to <code>sentry.io</code></li>
                </ol>

                <div className="mt-4">
                  <IonButton
                    expand="block"
                    fill="outline"
                    href="https://sentry.io/organizations/your-org/issues/"
                    target="_blank"
                  >
                    Open Sentry Dashboard
                  </IonButton>
                </div>
              </div>
            </IonCardContent>
          </IonCard>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SentryTestPage;
